import React from "react";

/**
 * JournalArtwork — a brand-native editorial poster used in place of
 * stock photography until the cinematic still-life imagery is
 * generated and dropped onto disk.
 *
 * Each motif is a single inline-SVG gold glyph centered on a warm
 * radial gradient. No external assets, no layout shift, no stock
 * photo risk. The motifs intentionally match the metaphors used in
 * each post's body.
 */
const Motif = ({ kind }) => {
  const stroke = "currentColor";
  switch (kind) {
    case "doorway":
    case "doorway-alt":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <rect x="60" y="40" width="80" height="140" rx="2" opacity="0.55" />
          <rect x="68" y="48" width="64" height="124" rx="1" opacity="0.9" />
          <circle cx="125" cy="110" r="2.5" fill={stroke} />
          <path d="M40 180 L160 180" opacity="0.4" />
        </svg>
      );
    case "compass":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <circle cx="100" cy="100" r="60" opacity="0.55" />
          <circle cx="100" cy="100" r="48" opacity="0.85" />
          <path d="M100 56 L108 100 L100 144 L92 100 Z" fill={stroke} opacity="0.85" />
          <circle cx="100" cy="100" r="3" fill={stroke} />
        </svg>
      );
    case "line-through-noise":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1">
          {Array.from({ length: 12 }).map((_, i) => (
            <line
              key={`n-${i}`}
              x1={20 + i * 14}
              y1={50 + ((i * 17) % 70)}
              x2={50 + i * 14}
              y2={70 + ((i * 23) % 60)}
              opacity="0.35"
            />
          ))}
          <line x1="20" y1="100" x2="180" y2="100" strokeWidth="1.8" opacity="0.95" />
        </svg>
      );
    case "candle":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <rect x="92" y="80" width="16" height="80" opacity="0.85" />
          <path d="M100 80 C 96 70, 96 60, 100 50 C 104 60, 104 70, 100 80 Z" fill={stroke} opacity="0.9" />
          <path d="M100 160 C 96 170, 96 180, 100 190 C 104 180, 104 170, 100 160 Z" fill={stroke} opacity="0.6" />
          <line x1="30" y1="170" x2="170" y2="170" opacity="0.4" />
        </svg>
      );
    case "road":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1">
          <path d="M30 180 L100 70 L170 180" opacity="0.55" />
          <path d="M100 70 L100 180" strokeDasharray="3 6" opacity="0.9" />
          <circle cx="100" cy="60" r="3" fill={stroke} />
        </svg>
      );
    case "kintsugi":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <rect x="50" y="50" width="100" height="100" rx="2" opacity="0.55" />
          <path d="M50 80 L120 110 L150 90" opacity="0.95" />
          <path d="M70 150 L100 120 L140 140" opacity="0.95" />
          <path d="M50 130 L80 100" opacity="0.7" />
        </svg>
      );
    case "constellation":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1">
          {[
            [50, 60], [90, 80], [130, 50], [160, 110],
            [110, 130], [70, 150], [40, 110],
          ].map(([cx, cy], i) => (
            <circle key={`c-${i}`} cx={cx} cy={cy} r="2.5" fill={stroke} opacity="0.85" />
          ))}
          <path d="M50 60 L90 80 L130 50 L160 110 L110 130 L70 150 L40 110 Z" opacity="0.5" />
        </svg>
      );
    case "clock":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <circle cx="100" cy="100" r="60" opacity="0.85" />
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
            const a = (i * Math.PI) / 6;
            const x1 = 100 + Math.cos(a) * 54;
            const y1 = 100 + Math.sin(a) * 54;
            const x2 = 100 + Math.cos(a) * 60;
            const y2 = 100 + Math.sin(a) * 60;
            return <line key={`t-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} opacity="0.7" />;
          })}
          <circle cx="100" cy="100" r="3" fill={stroke} />
        </svg>
      );
    case "mirror-chair":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <ellipse cx="100" cy="100" rx="56" ry="76" opacity="0.85" />
          <path d="M85 90 L85 130 L115 130 L115 90" opacity="0.7" />
          <path d="M80 130 L80 150 L120 150 L120 130" opacity="0.7" />
        </svg>
      );
    // ── Volume II motifs ──────────────────────────────────────────
    case "waiting-room":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <path d="M70 130 L70 90 L130 90 L130 130" opacity="0.85" />
          <path d="M60 130 L140 130 L140 150 L60 150 Z" opacity="0.7" />
          <line x1="30" y1="170" x2="170" y2="170" opacity="0.45" />
          <circle cx="100" cy="60" r="6" fill={stroke} opacity="0.9" />
        </svg>
      );
    case "background-apps":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1">
          {Array.from({ length: 16 }).map((_, i) => {
            const r = 4 + (i % 3);
            const x = 40 + (i % 4) * 36;
            const y = 40 + Math.floor(i / 4) * 36;
            return <rect key={`a-${i}`} x={x} y={y} width={14} height={14} rx="2" opacity={0.25 + (i % 5) * 0.05} />;
          })}
          <rect x="86" y="86" width="28" height="28" rx="3" fill={stroke} opacity="0.95" />
        </svg>
      );
    case "old-shoes":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <path d="M40 130 Q60 110 100 120 Q140 130 160 130 L160 145 Q140 150 100 150 Q60 145 40 145 Z" opacity="0.85" />
          <path d="M40 130 L40 145" opacity="0.75" />
          <line x1="58" y1="125" x2="58" y2="145" opacity="0.55" />
          <line x1="80" y1="122" x2="80" y2="148" opacity="0.55" />
          <line x1="120" y1="123" x2="120" y2="148" opacity="0.55" />
        </svg>
      );
    case "endurance":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <circle cx="100" cy="110" r="50" opacity="0.85" />
          <path d="M88 60 L112 60" opacity="0.85" />
          <path d="M100 60 L100 70" opacity="0.85" />
          <line x1="100" y1="110" x2="100" y2="78" opacity="0.95" />
          <line x1="100" y1="110" x2="124" y2="120" opacity="0.7" />
          <circle cx="100" cy="110" r="3" fill={stroke} />
        </svg>
      );
    case "house-moving":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <path d="M50 130 L50 80 L100 50 L150 80 L150 130" opacity="0.85" />
          <rect x="92" y="100" width="20" height="30" opacity="0.75" />
          <line x1="40" y1="160" x2="160" y2="160" opacity="0.45" />
          <rect x="55" y="138" width="22" height="18" opacity="0.55" />
          <circle cx="123" cy="115" r="1.4" fill={stroke} />
        </svg>
      );
    case "stage-light-off":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <path d="M85 40 L115 40 L120 60 L80 60 Z" opacity="0.85" />
          <path d="M80 60 L70 140 L130 140 L120 60 Z" opacity="0.3" />
          <circle cx="100" cy="55" r="3" fill={stroke} opacity="0.9" />
          <line x1="40" y1="170" x2="160" y2="170" opacity="0.45" />
        </svg>
      );
    case "coach-vs-critic":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <ellipse cx="78" cy="110" rx="22" ry="26" opacity="0.85" />
          <ellipse cx="122" cy="110" rx="22" ry="26" opacity="0.85" />
          <path d="M70 88 Q78 80 86 88" opacity="0.85" />
          <path d="M114 88 Q122 80 130 88" opacity="0.85" />
          <line x1="40" y1="150" x2="160" y2="150" opacity="0.45" />
        </svg>
      );
    case "flashlight":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <rect x="40" y="92" width="36" height="22" rx="2" opacity="0.85" />
          <rect x="76" y="98" width="10" height="10" opacity="0.95" />
          <path d="M86 103 L160 60 L160 150 Z" opacity="0.25" />
          <path d="M86 103 L160 103" opacity="0.55" strokeDasharray="3 5" />
        </svg>
      );
    case "key-and-door":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <circle cx="65" cy="105" r="14" opacity="0.85" />
          <line x1="78" y1="105" x2="150" y2="105" opacity="0.9" />
          <line x1="130" y1="105" x2="130" y2="120" opacity="0.85" />
          <line x1="140" y1="105" x2="140" y2="118" opacity="0.85" />
          <rect x="155" y="60" width="22" height="90" opacity="0.55" />
          <circle cx="170" cy="105" r="1.6" fill={stroke} />
        </svg>
      );
    case "fog-road":
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <path d="M50 170 L90 90 L110 90 L150 170 Z" opacity="0.6" />
          <line x1="100" y1="170" x2="100" y2="100" strokeDasharray="4 8" opacity="0.85" />
          {[0,1,2,3].map((i) => (
            <line key={`fg-${i}`} x1={30 + i * 40} y1={70 + i * 6} x2={70 + i * 40} y2={70 + i * 6} opacity={0.3 - i * 0.04} />
          ))}
          <circle cx="100" cy="78" r="5" fill={stroke} opacity="0.85" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 200 200" fill="none" stroke={stroke} strokeWidth="1.2">
          <circle cx="100" cy="100" r="48" opacity="0.6" />
          <circle cx="100" cy="100" r="36" opacity="0.85" />
        </svg>
      );
  }
};

const JournalArtwork = ({ motif, alt, aspect = "16/9", className = "" }) => (
  <div
    role="img"
    aria-label={alt}
    className={`journal-art ${className}`}
    style={{ aspectRatio: aspect }}
  >
    <div className="journal-art-glow" aria-hidden />
    <div className="journal-art-motif text-gold" aria-hidden>
      <Motif kind={motif} />
    </div>
    <div className="journal-art-grain" aria-hidden />
  </div>
);

export default JournalArtwork;
